/**
 * Workflow Transformers
 *
 * Centralized transformation functions for converting between API workflows
 * and UI-layer OrchWorkflows. This is the single source of truth for all
 * status mappings and optimistic updates.
 *
 * Why centralize:
 * - Single source of truth: All transformations use the same logic
 * - Easier to extend: New statuses only need statusMap update in workflowUtils
 * - Testable: Pure functions can be unit tested in isolation
 * - DRY: No inline status/orchStatus pairs scattered across mutations
 */

import type { Workflow as APIWorkflow, WorkflowStatus } from '@/shared/types'
import type { OrchWorkflow } from '../types'
import { statusMap } from './workflowUtils'

// ── API → OrchWorkflow Transformer ─────────────────────────────────────────────

/**
 * Canonical transformer that maps API Workflow → OrchWorkflow.
 * orchStatus is derived from statusMap (single source of truth).
 */
export function apiWorkflowToOrch(workflow: APIWorkflow): OrchWorkflow {
  return {
    ...workflow,
    orchStatus: statusMap[workflow.status] || 'PENDING',
  }
}

// ── Optimistic Update Transformers ─────────────────────────────────────────────
// These functions apply optimistic state changes before API calls complete.
// Each derives orchStatus from statusMap to maintain consistency.

/**
 * Optimistic update for approve action.
 * Transitions workflow to in_progress/EXECUTING.
 */
export function optimisticApprove(workflow: OrchWorkflow): OrchWorkflow {
  const newStatus: WorkflowStatus = 'in_progress'
  return {
    ...workflow,
    status: newStatus,
    orchStatus: statusMap[newStatus],
  }
}

/**
 * Optimistic update for reject action.
 * Transitions workflow to rejected/DONE.
 */
export function optimisticReject(workflow: OrchWorkflow): OrchWorkflow {
  const newStatus: WorkflowStatus = 'rejected'
  return {
    ...workflow,
    status: newStatus,
    orchStatus: statusMap[newStatus],
  }
}

/**
 * Optimistic update for execute action.
 * Transitions workflow to in_progress/EXECUTING.
 */
export function optimisticExecute(workflow: OrchWorkflow): OrchWorkflow {
  const newStatus: WorkflowStatus = 'in_progress'
  return {
    ...workflow,
    status: newStatus,
    orchStatus: statusMap[newStatus],
  }
}

/**
 * Optimistic update for retry action.
 * Transitions workflow to pending/PENDING.
 */
export function optimisticRetry(workflow: OrchWorkflow): OrchWorkflow {
  const newStatus: WorkflowStatus = 'pending'
  return {
    ...workflow,
    status: newStatus,
    orchStatus: statusMap[newStatus],
  }
}
